/**
 * Next goal: one line at the top of the Planets tab that walks a new player
 * through IPM's opening — the order the IPM wiki's New Starters guide gives —
 * then steps aside once the loop is running. The first unmet goal wins.
 */

import { PLANETS, FOLD_MIN_GALAXY_VALUE } from '../content/planets';
import { resource } from '../content/resources';
import { hasProject } from '../sim/projects';
import { roomsBuilt } from '../sim/rooms';
import { canStartChallenge } from '../sim/challenges';
import type { GameState } from '../sim/state';
import { fmtNum } from './format';

export interface Goal {
  readonly text: string;
  /** A button that takes you there: [label, data-act, data attributes]. */
  readonly go?: readonly [string, string, Readonly<Record<string, string | number>>];
}

function oresHeld(state: GameState): number {
  return Object.entries(state.run.hold).filter(([id]) => resource(id).kind === 'ore').reduce((sum, [, q]) => sum + q, 0);
}

export function nextGoal(state: GameState): Goal | null {
  const run = state.run;
  const owned = run.planets.filter((p) => p.owned).length;
  const projects = (id: string) => hasProject(state, id);
  const toProjects = ['Projects', 'openProjects', {}] as const;

  if (owned === 0) return { text: `Buy <b>${PLANETS[0]!.name}</b>, your first mining world.`, go: ['Wayfinder', 'select', { planet: 0 }] };
  if (run.lifetimeGc < 20 && oresHeld(state) >= 10) return { text: 'Ore is arriving in the Hold. <b>Sell it</b> for ◈ GC.', go: ['Sell ores', 'sellKind', { kind: 'ore' }] };
  if (owned === 1 && run.planets[0]!.levels.mine < 5) return { text: 'Upgrade Wayfinder. The tagged <b>Bottleneck</b> row is the best buy.', go: ['Wayfinder', 'select', { planet: 0 }] };
  if (owned < 3) return { text: `Buy <b>${PLANETS[owned]!.name}</b> next. A good rule: buy a new planet once your newest reaches Mining 12.`, go: [PLANETS[owned]!.name, 'select', { planet: owned }] };
  if (!projects('asteroid_miner')) return { text: 'Research <b>Asteroid Miner</b>: it opens the Smelter, and asteroids to tap for ore.', go: toProjects };
  if (!projects('smelter')) return { text: 'Research <b>Smelter</b>: 1,000 ore makes a bar worth far more.', go: toProjects };
  if (run.smelters.every((s) => !s.recipe)) return { text: 'Set your smelter to <b>Ferrite Bar</b> in the Forge.', go: ['Forge', 'tab', { tab: 'production' }] };
  if (!projects('management')) return { text: 'Research <b>Management</b>, then <b>Telescope 1</b> to see three more planets.', go: toProjects };
  if (!projects('telescope_1')) return { text: 'Research <b>Telescope 1</b> (it needs 5 Ferrite Bars) to see three more planets.', go: toProjects };
  if (!projects('crafter')) return { text: 'Research <b>Crafter</b>: items pay for most later projects.', go: toProjects };
  if (state.folds === 0 && run.galaxyValue < FOLD_MIN_GALAXY_VALUE) {
    return { text: `Grow <b>Galaxy Value</b> to ◈ 10M to Fold: ◈ ${fmtNum(run.galaxyValue)} so far. Planets and upgrades count.` };
  }
  if (state.folds === 0) return { text: 'You can <b>Fold</b>: sell this galaxy for TE, spend it at the Station and start again stronger.', go: ['Fold', 'tab', { tab: 'fold' }] };
  if (roomsBuilt(state) === 0 && state.te >= 3) return { text: 'Spend your TE on a <b>Room</b>: Engineering speeds up every planet.', go: ['Rooms', 'openRooms', {}] };
  if (state.challengesDone === 0 && canStartChallenge(state)) return { text: 'Challenges are open: a 24-hour side galaxy that pays <b>Void Shards</b> for the Matrix.', go: ['Challenge', 'station', { tab: 'challenge' }] };
  return null;
}
